import map from 'lodash/map';
import mean from 'lodash/mean';
import size from 'lodash/size';
import find from 'lodash/find';
import sumBy from 'lodash/sumBy';
import round from 'lodash/round';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';

import {
    CardData,
    CardSummaryData,
    HeadToHeadRecords,
    NormalizedMatchData,
} from '../../data_types/types';

import { STATUS_ROUND_COMPLETE } from '../const';


export const generateCardData = (myPID: string, matchData: NormalizedMatchData[]): CardData[]=> {
    const cardData: CardData[] = [];

    forEach(matchData, (match)=> {
        const card: CardData = {
            size: match.cardSize,
            win: includes(match.winners, myPID) && size(match.winners) === 1,
            draw: includes(match.winners, myPID) && size(match.winners) > 1,
            loss: !includes(match.winners, myPID),
            match_wins: 0,
            match_draws: 0,
            match_losses: 0,
        };

        const myPlayer = find(match.playerData, ['pID', myPID]);

        for (var playerData of match.playerData) {
            if (playerData.pID === myPID) {
                continue;
            }

            const didMyPlayerRetire = myPlayer!.status !== STATUS_ROUND_COMPLETE;
            const didOpponentRetire = playerData.status !== STATUS_ROUND_COMPLETE;

            if (didMyPlayerRetire && didOpponentRetire) {
                card['match_draws'] += 1;

            } else if (didMyPlayerRetire && !didOpponentRetire) {
                card['match_losses'] += 1;

            } else if (!didMyPlayerRetire && didOpponentRetire) {
                card['match_wins'] += 1;

            } else if (playerData.totalScore > myPlayer!.totalScore) {
                card['match_wins'] += 1;

            } else if (playerData.totalScore < myPlayer!.totalScore) {
                card['match_losses'] += 1;

            } else if (playerData.totalScore === myPlayer!.totalScore) {
                card['match_draws'] += 1;

            }
        };

        cardData.push(card);
    })

    return cardData;
}; 


export const generateCardSummaryData = (cardData: CardData[], headToHeadRecords: HeadToHeadRecords): CardSummaryData=> {
    const totalRounds = size(cardData);

    const twoPlayerCards = filter(cardData, ['size', 2]);
    const threePlayerCards = filter(cardData, ['size', 3]);
    const fourPlayerCards = filter(cardData, ['size', 4]);

    const twoPlayerCardsTotal = size(twoPlayerCards);
    const threePlayerCardsTotal = size(threePlayerCards);
    const fourPlayerCardsTotal = size(fourPlayerCards);

    const twoPlayerCardsPercent = round((twoPlayerCardsTotal / totalRounds) * 100, 0);
    const threePlayerCardsPercent = round((threePlayerCardsTotal / totalRounds) * 100, 0);
    const fourPlayerCardsPercent = round((fourPlayerCardsTotal / totalRounds) * 100, 0);

    const twoPlayerCardsMatchesTotal = twoPlayerCardsTotal;
    const threePlayerCardsMatchesTotal = threePlayerCardsTotal * 2;
    const fourPlayerCardsMatchesTotal = fourPlayerCardsTotal * 3;

    const totalMatches = twoPlayerCardsMatchesTotal + threePlayerCardsMatchesTotal + fourPlayerCardsMatchesTotal;
    const totalUniqueMatches = size(headToHeadRecords);

    const averageCardSize = round(mean(map(cardData, 'size')), 1);

    const overallCardWins = sumBy(cardData, 'win');
    const overallCardDraws = sumBy(cardData, 'draw');
    const overallCardLosses = sumBy(cardData, 'loss');

    const overallMatchWins = sumBy(cardData, 'match_wins');
    const overallMatchDraws = sumBy(cardData, 'match_draws');
    const overallMatchLosses = sumBy(cardData, 'match_losses');

    const overallCardWinsPercent = round((overallCardWins / totalRounds) * 100, 0);
    const overallCardDrawsPercent = round((overallCardDraws / totalRounds) * 100, 0);
    const overallCardLossesPercent = round((overallCardLosses / totalRounds) * 100, 0);

    const overallMatchWinsPercent = round((overallMatchWins / totalMatches) * 100, 0);
    const overallMatchDrawsPercent = round((overallMatchDraws / totalMatches) * 100, 0);
    const overallMatchLossesPercent = round((overallMatchLosses / totalMatches) * 100, 0);

    return {
        total_rounds: totalRounds,
        total_matches: totalMatches,
        total_unique_matches: totalUniqueMatches,
        average_card_size: averageCardSize,

        overall_card_wins: overallCardWins,
        overall_card_draws: overallCardDraws,
        overall_card_losses: overallCardLosses,
        overall_card_wins_percent: overallCardWinsPercent,
        overall_card_draws_percent: overallCardDrawsPercent,
        overall_card_losses_percent: overallCardLossesPercent,

        overall_match_wins: overallMatchWins,
        overall_match_draws: overallMatchDraws,
        overall_match_losses: overallMatchLosses,
        overall_match_wins_percent: overallMatchWinsPercent,
        overall_match_draws_percent: overallMatchDrawsPercent,
        overall_match_losses_percent: overallMatchLossesPercent,

        card_2_total: twoPlayerCardsTotal,
        card_3_total: threePlayerCardsTotal,
        card_4_total: fourPlayerCardsTotal,

        card_2_percent: twoPlayerCardsPercent,
        card_3_percent: threePlayerCardsPercent,
        card_4_percent: fourPlayerCardsPercent,

        card_2_matches_total: twoPlayerCardsMatchesTotal,
        card_3_matches_total: threePlayerCardsMatchesTotal,
        card_4_matches_total: fourPlayerCardsMatchesTotal,

        card: getCardSummaryData(
            twoPlayerCards,
            threePlayerCards,
            fourPlayerCards,
            twoPlayerCardsTotal,
            threePlayerCardsTotal,
            fourPlayerCardsTotal,
            totalRounds
        ),
        match: getMatchSummaryData(
            twoPlayerCards,
            threePlayerCards,
            fourPlayerCards,
            twoPlayerCardsMatchesTotal,
            threePlayerCardsMatchesTotal,
            fourPlayerCardsMatchesTotal,
            totalMatches
        )
    };
}; 


export const getCardSummaryData = (
    twoPlayerCards: CardData[],
    threePlayerCards: CardData[],
    fourPlayerCards: CardData[],
    twoPlayerCardsTotal: number,
    threePlayerCardsTotal: number,
    fourPlayerCardsTotal: number,
    totalRounds: number
)=> {
    const twoPlayerCardsWins = sumBy(twoPlayerCards, 'win');
    const threePlayerCardsWins = sumBy(threePlayerCards, 'win');
    const fourPlayerCardsWins = sumBy(fourPlayerCards, 'win');

    const twoPlayerCardsLosses = sumBy(twoPlayerCards, 'loss');
    const threePlayerCardsLosses = sumBy(threePlayerCards, 'loss');
    const fourPlayerCardsLosses = sumBy(fourPlayerCards, 'loss');

    const twoPlayerCardsDraws = sumBy(twoPlayerCards, 'draw');
    const threePlayerCardsDraws = sumBy(threePlayerCards, 'draw');
    const fourPlayerCardsDraws = sumBy(fourPlayerCards, 'draw');

    const twoPlayerCardsWinPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsWins / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsWinPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsWins / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsWinPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsWins / fourPlayerCardsTotal) * 100, 0);

    const twoPlayerCardsDrawPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsDraws / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsDrawPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsDraws / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsDrawPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsDraws / fourPlayerCardsTotal) * 100, 0);

    const twoPlayerCardsLossPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsLosses / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsLossPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsLosses / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsLossPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsLosses / fourPlayerCardsTotal) * 100, 0);

    return {
        card_2_wins: twoPlayerCardsWins,
        card_2_win_percent: twoPlayerCardsWinPercent,
        card_2_draws: twoPlayerCardsDraws,
        card_2_draw_percent: twoPlayerCardsDrawPercent,
        card_2_losses: twoPlayerCardsLosses,
        card_2_loss_percent: twoPlayerCardsLossPercent,

        card_3_wins: threePlayerCardsWins,
        card_3_win_percent: threePlayerCardsWinPercent,
        card_3_draws: threePlayerCardsDraws,
        card_3_draw_percent: threePlayerCardsDrawPercent,
        card_3_losses: threePlayerCardsLosses,
        card_3_loss_percent: threePlayerCardsLossPercent,

        card_4_wins: fourPlayerCardsWins,
        card_4_win_percent: fourPlayerCardsWinPercent,
        card_4_draws: fourPlayerCardsDraws,
        card_4_draw_percent: fourPlayerCardsDrawPercent,
        card_4_losses: fourPlayerCardsLosses,
        card_4_loss_percent: fourPlayerCardsLossPercent,
    };
};

export const getMatchSummaryData = (
    twoPlayerCards: CardData[],
    threePlayerCards: CardData[],
    fourPlayerCards: CardData[],
    twoPlayerCardsTotal: number,
    threePlayerCardsTotal: number,
    fourPlayerCardsTotal: number,
    totalRounds: number
)=> {
    const twoPlayerCardsWins = sumBy(twoPlayerCards, 'match_wins');
    const threePlayerCardsWins = sumBy(threePlayerCards, 'match_wins');
    const fourPlayerCardsWins = sumBy(fourPlayerCards, 'match_wins');

    const twoPlayerCardsLosses = sumBy(twoPlayerCards, 'match_losses');
    const threePlayerCardsLosses = sumBy(threePlayerCards, 'match_losses');
    const fourPlayerCardsLosses = sumBy(fourPlayerCards, 'match_losses');

    const twoPlayerCardsDraws = sumBy(twoPlayerCards, 'match_draws');
    const threePlayerCardsDraws = sumBy(threePlayerCards, 'match_draws');
    const fourPlayerCardsDraws = sumBy(fourPlayerCards, 'match_draws');

    const twoPlayerCardsWinPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsWins / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsWinPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsWins / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsWinPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsWins / fourPlayerCardsTotal) * 100, 0);

    const twoPlayerCardsDrawPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsDraws / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsDrawPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsDraws / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsDrawPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsDraws / fourPlayerCardsTotal) * 100, 0);

    const twoPlayerCardsLossPercent = twoPlayerCardsTotal === 0 ? 0 : round((twoPlayerCardsLosses / twoPlayerCardsTotal) * 100, 0);
    const threePlayerCardsLossPercent = threePlayerCardsTotal === 0 ? 0 : round((threePlayerCardsLosses / threePlayerCardsTotal) * 100, 0);
    const fourPlayerCardsLossPercent = fourPlayerCardsTotal === 0 ? 0 : round((fourPlayerCardsLosses / fourPlayerCardsTotal) * 100, 0);

    return {
        card_2_wins: twoPlayerCardsWins,
        card_2_win_percent: twoPlayerCardsWinPercent,
        card_2_draws: twoPlayerCardsDraws,
        card_2_draw_percent: twoPlayerCardsDrawPercent,
        card_2_losses: twoPlayerCardsLosses,
        card_2_loss_percent: twoPlayerCardsLossPercent,

        card_3_wins: threePlayerCardsWins,
        card_3_win_percent: threePlayerCardsWinPercent,
        card_3_draws: threePlayerCardsDraws,
        card_3_draw_percent: threePlayerCardsDrawPercent,
        card_3_losses: threePlayerCardsLosses,
        card_3_loss_percent: threePlayerCardsLossPercent,

        card_4_wins: fourPlayerCardsWins,
        card_4_win_percent: fourPlayerCardsWinPercent,
        card_4_draws: fourPlayerCardsDraws,
        card_4_draw_percent: fourPlayerCardsDrawPercent,
        card_4_losses: fourPlayerCardsLosses,
        card_4_loss_percent: fourPlayerCardsLossPercent,
    };
};
